import {
    nextBestActionCopy,
    ConfirmationStepProps,
    ValidNextBestActions
} from './data';
import {
    NextBestAction,
    NextBestActionClaimTrackedPropertyAsHomeTypeEnum,
    NextBestActionEnum
} from '../../../../graphql.generated';

export const useConfirmationStep = (
    nextBestActionData?: NextBestAction | null
) => {
    const getContent = (): ConfirmationStepProps => {
        const action = nextBestActionData?.action;
        const extraDetails = nextBestActionData?.extraDetails;
        const defaultValues = {
            title: '',
            url: '',
            copy: '',
            cta: ''
        };

        if (
            !nextBestActionData ||
            !action ||
            action === NextBestActionEnum.EmailMarketingConsent ||
            action === NextBestActionEnum.UpdateYourSituation
        ) {
            return defaultValues;
        }

        const content = nextBestActionCopy[action];

        if (
            action === NextBestActionEnum.ClaimHome &&
            extraDetails?.__typename ===
                'NextBestActionClaimTrackedPropertyAsHomeExtraDetails'
        ) {
            const totalCount = extraDetails.total;
            const propertyAddress = extraDetails.address;
            const singleType =
                NextBestActionClaimTrackedPropertyAsHomeTypeEnum.Single;
            const multipleType =
                NextBestActionClaimTrackedPropertyAsHomeTypeEnum.Multiple;

            if (extraDetails.claimHomeType === singleType) {
                content[extraDetails.claimHomeType].title = content[
                    singleType
                ].title.replace('{address}', propertyAddress || '');
            }

            if (extraDetails.claimHomeType === multipleType) {
                content[extraDetails.claimHomeType].title = content[
                    multipleType
                ].title.replace('{number}', String(totalCount || 0));
            }

            return content[extraDetails.claimHomeType];
        }

        if (
            action === NextBestActionEnum.ContactableHomeowners &&
            extraDetails?.__typename ===
                'NextBestActionContactableHomeownersExtraDetails'
        ) {
            const propertyAddress = extraDetails.homeownerAddress;
            content.DEFAULT.title = content.DEFAULT.title.replace(
                '{address}',
                propertyAddress || ''
            );
        }

        if (
            action === NextBestActionEnum.AcquireNewHomeowners &&
            extraDetails?.__typename ===
                'NextBestActionAcquireNewHomeownersExtraDetails'
        ) {
            return content[extraDetails?.acquireNewHomeownersType];
        }

        if (
            action === NextBestActionEnum.NewHomeLeads &&
            extraDetails?.__typename === 'NextBestActioNewHomeLeadsExtraDetails'
        ) {
            return content[extraDetails?.newHomeLeadsType];
        }

        if (action === NextBestActionEnum.ForSaleEngagement) {
            // Randomly show 1 out of 3 messages
            const randomNumber = Math.floor(Math.random() * 3);
            const itemKeys = Object.keys(content)[randomNumber];

            return content[itemKeys];
        }

        if (
            action === NextBestActionEnum.VendorLeadNudge &&
            extraDetails?.__typename ===
                'NextBestActionVendorLeadNudgeExtraDetails'
        ) {
            if (extraDetails?.vendorLeadNudgeType && extraDetails.address) {
                const type = `${extraDetails?.vendorLeadNudgeType}_WITH_ADDRESS`;
                const withAddressContent = content[type];

                if (typeof withAddressContent.title === 'string') {
                    withAddressContent.title = withAddressContent.title.replace(
                        '{address}',
                        extraDetails.address
                    );
                }

                if (typeof withAddressContent.url === 'string') {
                    withAddressContent.url = withAddressContent.url.replace(
                        '{uprn}',
                        extraDetails.uprn || ''
                    );
                }

                return withAddressContent;
            }

            // Fallback to default content if no address in extra details
            return content[extraDetails?.vendorLeadNudgeType];
        }

        return nextBestActionCopy?.[action]?.DEFAULT || defaultValues;
    };

    const getTrackingData = () => {
        const action = nextBestActionData?.action;
        const trackingMap: {
            [key in ValidNextBestActions]: string;
        } = {
            [NextBestActionEnum.VendorLeadNudge]: 'vendor_lead',
            [NextBestActionEnum.AcquireNewHomeowners]: 'track_home',
            [NextBestActionEnum.AppDownloads]: 'app_download',
            [NextBestActionEnum.ClaimHome]: 'claim_tracked_property_as_home',
            [NextBestActionEnum.ContactableHomeowners]:
                'property_insight_consent',
            [NextBestActionEnum.ConvertSavedSearchToAlerts]:
                'saved_search_to_alert',
            [NextBestActionEnum.ForSaleEngagement]: 'for_sale',
            [NextBestActionEnum.NewHomeLeads]: 'new_homes',
            [NextBestActionEnum.Remortgage]: 'remortgage'
        };

        if (
            !action ||
            action === NextBestActionEnum.EmailMarketingConsent ||
            action === NextBestActionEnum.UpdateYourSituation
        ) {
            return '';
        }

        return trackingMap[action] || '';
    };

    return {
        getContent,
        getTrackingData
    };
};
